// ─────────────────────────────────────────────────────────────────────────
// financeApprovalData — resolves the FULL finance picture for a submission.
//
// Why this exists: this backend has NO by-id detail route (module-data-detail
// 404s for submissions/clients/jobs) and its list projections drop both the
// reference ids and the rate fields. The ONE endpoint that returns a record's
// real values is /admin/form-groups?module=&id= — it carries every configured
// field value PLUS `recordMeta` (clientId, jobId, applicantId …). So that is
// the authoritative source here, and the remaining joins are resolved from the
// list endpoints that do work.
//
// Verified against zinnext-dev-api-v1:
//   form-groups(submission) → Employer group { employerId, location(id), tax },
//                             Rate Details  { candidateBudget.*, proposedRate.* },
//                             Job Information { jobTitle, clientName, jobRefrenceId }
//   locations list          → { _id, name, taxValue }   ← W2 tax + location NAME
//   employers list          → { _id, empCompanyName, empCompanyLocation }
//   clients   list          → ONLY the configured list columns (clientName,
//                             clientRefrenceId, clientType, websiteUrl,
//                             federalId, paymentTerms, clientStatus). An
//                             earlier comment here claimed this returned the
//                             full document — it does NOT (proven by
//                             cmd/probe-client-vms-formgroups), which is why
//                             the client VMS discount kept resolving to 0%.
//                             The fee is read via fullRecordOf('clients', …).
//   jobs      detail        → scoped to the caller's recruiter hierarchy, so
//                             jobPayDetails (Client Job Rate) can come back
//                             empty; also read via fullRecordOf('jobs', …).
// ─────────────────────────────────────────────────────────────────────────
import { idOf, flattenGroups, findEmployerRow, unflattenRecord } from './financeApprovalEngine.js';
import { fetchJsonWithAuth } from '../../services/authApi';
import { AUTH_URL } from '../../services/apiConfig';
import { getModuleDataList } from '../../services/moduleDataApi';
import { getModuleDataDetail } from '../../services/detailedViewApi';

const norm = (v) => String(v ?? '').trim().toLowerCase();

function extractRows(json) {
  const d = json?.data ?? json;
  for (const key of ['data', 'rows', 'records', 'items', 'list']) {
    if (Array.isArray(d?.[key])) return d[key];
  }
  return Array.isArray(d) ? d : [];
}

// listOf — CAUTION: /module-data-list applies a Mongo PROJECTION built from the
// module's configured list-view columns (see moduleDataController.go's
// "Projection: visible fields + sort field + tab field"). Any field that isn't
// a visible list column is STRIPPED from every row — e.g. clients come back
// without vmsFeeHistory/vmsPercentage/vmsFee, and jobs without jobPayDetails.
// So this is only safe for locating a record and reading its list-visible
// columns; never trust it for a value the finance math depends on — use
// fullRecordOf() for those.
const listOf = (module) => getModuleDataList(module, 300, 0).then(extractRows).catch(() => []);
const byId = (id) => (r) => id && idOf(r._id ?? r.id) === String(id);

// rawRecordOf — the STORED document, projection-free and config-free.
// /module/list?module=&id= runs a plain Find with no projection (see
// ModuleListHandler), so it is the only path that returns fields which are
// neither list-view columns NOR configured form fields. `signatureEmail` is
// exactly that: it is on every submission but appears in no form group, so
// flattenGroups can never surface it.
const rawRecordOf = (module, id) => (
  !id
    ? Promise.resolve({})
    : fetchJsonWithAuth(
      AUTH_URL,
      `/module/list?module=${encodeURIComponent(module)}&id=${encodeURIComponent(id)}`,
    )
      .then((json) => {
        const data = json?.data ?? json ?? {};
        const rows = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
        return rows[0] ?? {};
      })
      .catch(() => ({}))
);

// Users are served by the auth API rather than the generic module list.
// Resolve only the submission creator instead of loading the whole user list.
const userById = (userId) => (
  userId === undefined || userId === null || userId === ''
    ? Promise.resolve(null)
    : fetchJsonWithAuth(
      AUTH_URL,
      `/get-detailed-view?userId=${encodeURIComponent(userId)}`,
    ).then((json) => json?.data ?? json ?? null).catch(() => null)
);

function userDisplayName(user) {
  if (!user) return '';
  const firstName = user.first_name ?? user.firstName ?? user.FIRST_NAME;
  const lastName = user.last_name ?? user.lastName ?? user.LAST_NAME;
  return [firstName, lastName].filter(Boolean).join(' ').trim()
    || user.user_name
    || user.userName
    || user.username
    || user.email
    || '';
}

// payDetailsOf — a source's jobPayDetails as a flat rate object. The field is
// an appendHistory container, so a raw record holds the whole history ARRAY;
// the LAST entry is the current rate. A source that already carries a flat
// object (the form-groups path, unwrapped server-side) passes through.
function payDetailsOf(source) {
  const pay = source?.jobPayDetails;
  const entry = Array.isArray(pay) ? pay[pay.length - 1] : pay;
  return (entry && typeof entry === 'object' && !Array.isArray(entry)) ? entry : {};
}

// fullRecordOf — the record's REAL stored values, projection-free.
// /admin/form-groups?module=&id= is the one endpoint that returns every
// CONFIGURED field's actual value (this file's header explains why), and it
// resolves append-history groups server-side to their latest entry
// (AttachHistoryGroupValues) — so a client's current VMS fee arrives already
// unwrapped from vmsFeeHistory[]. recordMeta is merged in for the scalar
// linkage ids (clientId/jobId/…) that are never form fields.
async function fullRecordOf(module, id) {
  if (!id) return {};
  const payload = await getFormGroupsRaw(module, id).catch(() => ({}));
  return {
    ...unflattenRecord(flattenGroups(payload.groups ?? [])),
    ...(payload.recordMeta ?? {}),
  };
}

// getFormGroupsRaw — the package's getFormGroups() drops `recordMeta` (it only
// returns the groups array), so we fetch the raw payload here.
export async function getFormGroupsRaw(module, id) {
  const params = new URLSearchParams({ module });
  if (id) params.set('id', id);
  const json = await fetchJsonWithAuth(AUTH_URL, `/admin/form-groups?${params.toString()}`);
  return json?.data ?? json ?? {};
}

// resolveSubmissionFinance — the one call the finance surfaces use.
// Returns { record, lookups, display } ready for resolveFinanceInputs().
export async function resolveSubmissionFinance(submissionId, seed = {}) {
  const payload = await getFormGroupsRaw('submission', submissionId).catch(() => ({}));
  const groups = payload.groups ?? [];
  const meta = payload.recordMeta ?? {};
  const flat = flattenGroups(groups);
  const employerRow = findEmployerRow(groups);

  const submissionClientId = idOf(meta.clientId ?? flat.clientId ?? seed.clientId);
  const jobId = idOf(meta.jobId ?? flat.jobId ?? seed.jobId);
  const employerId = idOf(employerRow.employerId);
  const createdBy = meta.createdBy ?? flat.createdBy ?? seed.createdBy;
  // "locationId" is the field's real stored key (see Form Groups → submissions
  // → Employer → Location); "location" only ever existed as an old config
  // alias, kept as a fallback for rows written before that field existed.
  const locationRef = employerRow.locationId ?? employerRow.location;

  const [
    jobJson, jobsList, clients, employers, locations, engagements, jobFull, submittedByUser, submissionRaw,
  ] = await Promise.all([
    jobId ? getModuleDataDetail('jobs', jobId).catch(() => null) : Promise.resolve(null),
    listOf('jobs'),
    listOf('clients'),
    listOf('employers'),
    listOf('locations'),
    // workforceEngagements is a plain schema-free module too (same /module-data-list
    // path as everything else here) — once this submission has moved to
    // onboarding, its engagement carries a clientId stamped at movement-creation
    // time, one more independent path to the right client alongside the job.
    listOf('workforceEngagements'),
    // Authoritative, projection-free job values — jobPayDetails (the Client
    // Job Rate) is NOT a list-view column, so the list rows above never carry
    // it, and getJobDetailView below is scoped to the caller's own recruiter
    // hierarchy (thin/empty for a manager approving a job they aren't
    // assigned to). Configured in Form Groups → jobs → "rate_details".
    fullRecordOf('jobs', jobId),
    userById(createdBy),
    // The submission's own stored document — for signatureEmail, which no
    // form group configures and no list projection keeps (see rawRecordOf).
    rawRecordOf('submissions', submissionId),
  ]);

  const jobData = jobJson?.data ?? jobJson ?? {};
  const jobDetail = jobData.record ?? jobData.item ?? jobData ?? {};
  const jobListMatch = jobsList.find(byId(jobId)) ?? {};
  // Widest-to-narrowest: projected list row → scoped legacy detail → the
  // projection-free configured values, which win. jobPayDetails is merged
  // KEY-WISE (not replaced) so a rate present in only one source survives.
  // Each source is run through payDetailsOf first: jobs.jobPayDetails is an
  // appendHistory container, so the RAW sources (list row, legacy detail) hold
  // the whole history ARRAY, and spreading an array yields {0: {...}} — the
  // rate keys silently vanish. Take the last entry (the current rate) instead.
  const job = { ...jobListMatch, ...jobDetail, ...jobFull };
  job.jobPayDetails = {
    ...payDetailsOf(jobListMatch),
    ...payDetailsOf(jobDetail),
    ...payDetailsOf(jobFull),
  };
  // If this submission has moved to onboarding, its workforceEngagement
  // carries its own clientId (stamped at movement-creation time from the job)
  // — a third independent path to the client, alongside the job and the
  // submission's own field, for whichever one didn't drop out.
  const engagementMatch = (Array.isArray(engagements) ? engagements : [])
    .find((e) => idOf(e.submissionId) === String(submissionId));
  // The real link is submission → job → client: jobs.clientId is the
  // authoritative source (a job's client assignment is the one thing that
  // can change after a submission was created, so the submission's own
  // clientId can drift stale) — resolve it FIRST, off the job we just
  // fetched, then the linked engagement's own clientId, and only fall back to
  // the submission's own clientId when neither is available.
  const clientId = idOf(job.clientId) || idOf(engagementMatch?.clientId) || submissionClientId;
  // Name fallback — last resort for a submission/job whose clientId is missing
  // or malformed entirely. NOTE clientName is NOT unique here ("TCS" alone has
  // 4 documents; "Mphasis"/"Wipro" are duplicated too), so this can land on
  // the wrong twin. It only decides WHICH id to re-read below, never the VMS
  // value itself, so the blast radius is a wrong client name rather than a
  // silently wrong discount — but prefer fixing the duplicate client records.
  const clientNameHint = flat.clientName ?? seed.clientName ?? job.clientName;
  const nameMatches = clientNameHint
    ? clients.filter((c) => norm(c.clientName) === norm(clientNameHint))
    : [];
  const clientRow = clients.find(byId(clientId)) ?? nameMatches[0] ?? {};
  // THE fix for "client discount shows 0%": the row above came from
  // /module-data-list, whose projection keeps only the configured list
  // columns (clientName, clientRefrenceId, clientType, websiteUrl, federalId,
  // paymentTerms, clientStatus) — the VMS fee is in NONE of them, so that row
  // is structurally incapable of carrying it no matter which client we match.
  // Re-read the same client projection-free for its real, current VMS fee.
  const clientFull = await fullRecordOf('clients', idOf(clientRow._id ?? clientRow.id) || clientId);
  const client = { ...clientRow, ...clientFull };
  const employer = employers.find(byId(employerId)) ?? {};
  // location may be stored as an id OR a name — match either.
  const location = locations.find(byId(locationRef))
    ?? locations.find((l) => norm(l.name) === norm(locationRef))
    ?? {};

  // The synthetic submission: all flat field values + the employer row shaped
  // as the array the engine expects. signatureEmail is filled in from the raw
  // document ONLY when nothing else supplied it — the raw doc is deliberately
  // not merged wholesale, since its append-history fields (proposedRate,
  // candidateRate …) are arrays the engine does not expect here.
  const submission = {
    ...(submissionRaw.signatureEmail ? { signatureEmail: submissionRaw.signatureEmail } : {}),
    ...seed,
    ...flat,
    submission_current_employer: [employerRow],
  };

  return {
    record: { submission, job, client, employer },
    lookups: {
      // Legacy fallback only — the engine now prefers client.vmsPercentage /
      // client.vmsPercentageType (the Client form's current, admin-edited
      // fields) directly off `client` above; this covers clients that predate
      // that form group and were never re-saved under it.
      clientVmsFee: client.vmsFee ?? flat.clientVmsFee ?? flat.vmsFee,
      // W2 tax: inline on the employer row, else the matched location's taxValue.
      locationTaxPercent: employerRow.tax ?? location.taxValue,
    },
    display: {
      clientName: flat.clientName ?? client.clientName,
      jobTitle: flat.jobTitle ?? job.jobTitle,
      reqId: meta.jobClientRefrenceId ?? flat.jobRefrenceId ?? meta.jobRefrenceId ?? meta.mspReqId,
      jobLocation: flat.jobLocation ?? job.jobLocation,
      employerName: employer.empCompanyName,
      employerLocation: location.name ?? employer.empCompanyLocation ?? locationRef,
      // Submitted By — the submission's OWN signature wins.
      // signatureEmail is stamped from the logged-in recruiter at submit time,
      // so it is the record's first-hand account of who submitted it. It beats
      // the createdBy user lookup, which is a numeric audit stamp that can
      // resolve to a different account than the one that signed (an admin, an
      // importer, a shared/stale id) — and did: createdBy 149 resolved to
      // revinard22@gmail.com on a submission signed sam@realtekconsulting.net.
      // The two removed fallbacks were not submitters at all: recruiterName is
      // the EMPLOYER row's recruiter contact and submitSource is the
      // submission's source ("Internal"), read as `vendor` by both popups.
      submittedBy: submissionRaw.signatureEmail
        || flat.signatureEmail
        || seed.signatureEmail
        || userDisplayName(submittedByUser),
      email: flat.email,
      candidateName: [flat.firstName, flat.lastName].filter(Boolean).join(' ') || flat.candidateFullName,
      createdAt: meta.createdAt,
      submissionId: meta.submissionId,
    },
    // Expose the linked engagement separately for API payload construction.
    // It is intentionally not merged into record, so manager re-approval UI
    // does not render onboarding referral details.
    engagement: engagementMatch ?? null,
    meta,
  };
}

export { idOf, flattenGroups, findEmployerRow };
